// src/pages/ExpenseTracker.jsx — Expense & Income Tracker
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../utils/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: 'INR', symbol: '₹',    name: 'Indian Rupee' },
  { code: 'USD', symbol: '$',    name: 'US Dollar' },
  { code: 'EUR', symbol: '€',    name: 'Euro' },
  { code: 'GBP', symbol: '£',    name: 'British Pound' },
  { code: 'JPY', symbol: '¥',    name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$',   name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$',   name: 'Canadian Dollar' },
  { code: 'SGD', symbol: 'S$',   name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'CHF', symbol: 'CHF',  name: 'Swiss Franc' },
];

const getCurrency   = (code) => CURRENCIES.find(c => c.code === (code || 'INR').toUpperCase()) || CURRENCIES[0];
const currSymbol    = (code) => getCurrency(code).symbol;
const fmtAmount     = (v, currency) => {
  const c = getCurrency(currency);
  const locale = c.code === 'INR' ? 'en-IN' : 'en-US';
  return c.symbol + Number(v || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const today = () => new Date().toISOString().split('T')[0];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const CATEGORY_COLORS = [
  '#4caf50','#2196f3','#f44336','#ff9800','#9c27b0',
  '#00bcd4','#607d8b','#e91e63','#795548','#009688',
];

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:    { padding: '24px', maxWidth: '1200px', margin: '0 auto', color: '#e0e0e0' },
  h1:      { fontSize: '22px', fontWeight: 700, marginBottom: '4px', color: '#fff' },
  subtitle:{ fontSize: '13px', color: '#888', marginBottom: '24px' },

  cardRow: { display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' },
  card: {
    flex: '1 1 160px', background: '#1e1e2e', borderRadius: '10px',
    padding: '16px 18px', border: '1px solid #2a2a3e',
  },
  cardLabel: { fontSize: '11px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: '22px', fontWeight: 700 },

  sectionHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '12px', cursor: 'pointer', userSelect: 'none',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#ccc' },
  section: {
    background: '#1e1e2e', borderRadius: '10px', border: '1px solid #2a2a3e',
    padding: '18px 20px', marginBottom: '16px',
  },

  filterRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  filterLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' },
  select: {
    background: '#16162a', border: '1px solid #333', borderRadius: '6px',
    color: '#ddd', padding: '6px 10px', fontSize: '13px', cursor: 'pointer',
  },

  btnPrimary: {
    background: '#3a3aff', color: '#fff', border: 'none', borderRadius: '6px',
    padding: '7px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
  },
  btnSecondary: {
    background: '#2a2a3e', color: '#ccc', border: '1px solid #333', borderRadius: '6px',
    padding: '7px 14px', fontSize: '13px', cursor: 'pointer',
  },
  btnDanger: {
    background: 'transparent', color: '#f44336', border: '1px solid #f44336',
    borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
  },
  btnSmall: {
    background: '#2a2a3e', color: '#aaa', border: '1px solid #333', borderRadius: '5px',
    padding: '3px 9px', fontSize: '12px', cursor: 'pointer',
  },
  btnPill: (active) => ({
    padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    cursor: 'pointer', border: '1px solid',
    background:   active ? '#0c4a6e' : 'transparent',
    borderColor:  active ? '#38bdf8' : '#334155',
    color:        active ? '#38bdf8' : '#64748b',
  }),

  label: { fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' },
  input: {
    background: '#16162a', border: '1px solid #333', borderRadius: '6px',
    color: '#ddd', padding: '7px 10px', fontSize: '13px', width: '100%', boxSizing: 'border-box',
  },

  tableWrap: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: '1px solid #2a2a3e', padding: '8px 10px',
  },
  td:     { padding: '8px 10px', borderBottom: '1px solid #1a1a2a', verticalAlign: 'middle' },
  trMisc: { background: 'rgba(255,193,7,0.06)' },

  badgeIncome:  { background: 'rgba(76,175,80,0.15)',  color: '#4caf50', borderRadius: '4px', padding: '2px 8px',  fontSize: '11px', fontWeight: 600 },
  badgeExpense: { background: 'rgba(244,67,54,0.15)',  color: '#f44336', borderRadius: '4px', padding: '2px 8px',  fontSize: '11px', fontWeight: 600 },
  badgeMisc:    { background: 'rgba(255,193,7,0.15)',  color: '#ffc107', borderRadius: '4px', padding: '2px 7px',  fontSize: '10px', fontWeight: 600, marginLeft: '4px' },
  badgeGlobal:  { background: 'rgba(100,181,246,0.12)',color: '#64b5f6', borderRadius: '4px', padding: '2px 7px',  fontSize: '10px', fontWeight: 600 },
  badgeCurr:    { background: '#12122a', color: '#94a3b8', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: 700, marginLeft: '4px' },

  flash: (type) => ({
    background: type === 'error' ? 'rgba(244,67,54,0.12)' : 'rgba(76,175,80,0.12)',
    color:      type === 'error' ? '#f44336' : '#4caf50',
    border:     `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`,
    borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px',
  }),
  dot: (color) => ({
    display: 'inline-block', width: '8px', height: '8px',
    borderRadius: '50%', background: color, marginRight: '6px',
  }),
};

// ── Flash ─────────────────────────────────────────────────────────────────────

function Flash({ msg, onDismiss }) {
  if (!msg) return null;
  return (
    <div style={S.flash(msg.type)}>
      {msg.text}
      <span style={{ float: 'right', cursor: 'pointer' }} onClick={onDismiss}>✕</span>
    </div>
  );
}

// ── Summary cards ─────────────────────────────────────────────────────────────
// breakdown comes from the backend: [{currency, income, expense, savings}, ...]
// balances is the bank balance array so we can compute per-currency opening balances

function SummaryCards({ summary, balances }) {
  const breakdown = summary.breakdown || [];

  // Per-currency opening balance map built from bank accounts
  const openByCurr = {};
  for (const b of balances) {
    const c = (b.currency || 'INR').toUpperCase();
    openByCurr[c] = (openByCurr[c] || 0) + parseFloat(b.balance || 0);
  }

  if (breakdown.length === 0) {
    return (
      <div style={{ ...S.card, marginBottom: '24px', color: '#555', fontSize: '13px', textAlign: 'center' }}>
        No transactions recorded for this period.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {breakdown.map((b) => {
        const curr     = (b.currency || 'INR').toUpperCase();
        const openBal  = openByCurr[curr] || 0;
        const hasOpen  = openBal !== 0;
        const savColor = b.savings >= 0 ? '#4caf50' : '#f44336';

        return (
          <div key={curr} style={{ marginBottom: '20px' }}>
            {/* Currency header */}
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', fontWeight: 700 }}>
              {curr} — {getCurrency(curr).name}
            </div>

            <div style={S.cardRow}>
              {hasOpen && (
                <div style={S.card}>
                  <div style={S.cardLabel}>Opening Balance</div>
                  <div style={{ ...S.cardValue, color: '#64b5f6' }}>{fmtAmount(openBal, curr)}</div>
                </div>
              )}
              <div style={S.card}>
                <div style={S.cardLabel}>Total Income</div>
                <div style={{ ...S.cardValue, color: '#4caf50' }}>{fmtAmount(b.income, curr)}</div>
              </div>
              <div style={S.card}>
                <div style={S.cardLabel}>Total Expenses</div>
                <div style={{ ...S.cardValue, color: '#f44336' }}>{fmtAmount(b.expense, curr)}</div>
              </div>
              <div style={S.card}>
                <div style={S.cardLabel}>Net Savings</div>
                <div style={{ ...S.cardValue, color: savColor }}>{fmtAmount(b.savings, curr)}</div>
              </div>
              {hasOpen && (
                <div style={S.card}>
                  <div style={S.cardLabel}>Closing Balance</div>
                  <div style={{ ...S.cardValue, color: '#fff' }}>{fmtAmount(openBal + b.savings, curr)}</div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Converted totals (multi-currency only) ────────────────────────────────────

function ConvertedSummary({ summary, baseCurrency, setBaseCurrency }) {
  const PILLS = ['INR', 'USD', 'EUR', 'GBP', 'SGD', 'AED'];
  return (
    <div style={{ ...S.section, marginBottom: '24px' }}>
      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', fontWeight: 700 }}>
        Converted Totals
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
        <span style={{ fontSize: '11px', color: '#64748b', marginRight: '4px' }}>Convert to:</span>
        {PILLS.map(c => (
          <button key={c} onClick={() => setBaseCurrency(baseCurrency === c ? '' : c)} style={S.btnPill(baseCurrency === c)}>
            {c}
          </button>
        ))}
      </div>

      {baseCurrency && summary.base_currency ? (
        <div style={S.cardRow}>
          <div style={S.card}>
            <div style={S.cardLabel}>Income ({summary.base_currency})</div>
            <div style={{ ...S.cardValue, color: '#4caf50' }}>{fmtAmount(summary.converted_total_income, summary.base_currency)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Expenses ({summary.base_currency})</div>
            <div style={{ ...S.cardValue, color: '#f44336' }}>{fmtAmount(summary.converted_total_expense, summary.base_currency)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Net ({summary.base_currency})</div>
            <div style={{ ...S.cardValue, color: summary.converted_savings >= 0 ? '#4caf50' : '#f44336' }}>
              {fmtAmount(summary.converted_savings, summary.base_currency)}
            </div>
          </div>
        </div>
      ) : baseCurrency ? (
        <div style={{ fontSize: '12px', color: '#64748b' }}>Fetching live rates…</div>
      ) : (
        <div style={{ fontSize: '12px', color: '#555' }}>
          Pick a currency above to see totals converted using live exchange rates.
        </div>
      )}
    </div>
  );
}

// ── Category management ───────────────────────────────────────────────────────

function CategoryManager({ categories, onAdd, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'expense', color: CATEGORY_COLORS[0] });
  const [adding, setAdding] = useState(false);

  const globals = categories.filter(c => c.is_global);
  const mine    = categories.filter(c => !c.is_global);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    await onAdd(form);
    setForm({ name: '', type: 'expense', color: CATEGORY_COLORS[0] });
    setAdding(false);
  };

  const CatList = ({ label, list, isGlobal }) => (
    <div style={{ flex: '1 1 200px' }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      {list.length === 0 && <div style={{ color: '#444', fontSize: '13px' }}>None</div>}
      {list.map(cat => (
        <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={S.dot(cat.color)} />
          <span style={{ fontSize: '13px', flex: 1 }}>{cat.name}</span>
          {isGlobal
            ? <span style={S.badgeGlobal}>Global</span>
            : <button style={S.btnDanger} onClick={() => onDelete(cat.id)}>✕</button>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Categories</span>
        <span style={{ color: '#555', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          <div style={{ marginBottom: '6px' }}>
            <div style={S.label}>Add Category</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              <input
                style={{ ...S.input, flex: '1 1 140px' }}
                placeholder="Category name"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <select style={S.select} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {CATEGORY_COLORS.map(c => (
                  <div key={c} onClick={() => setForm(p => ({ ...p, color: c }))} style={{
                    width: '16px', height: '16px', borderRadius: '50%', background: c, cursor: 'pointer',
                    border: form.color === c ? '2px solid #fff' : '2px solid transparent',
                  }} />
                ))}
              </div>
              <button style={S.btnPrimary} onClick={handleAdd} disabled={adding}>
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </div>
          </div>

          {mine.length > 0 && (
            <div style={{ marginBottom: '16px', marginTop: '16px' }}>
              <div style={{ fontSize: '11px', color: '#64b5f6', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 700 }}>Your Categories</div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <CatList label="Income"  list={mine.filter(c => c.type === 'income')}  isGlobal={false} />
                <CatList label="Expense" list={mine.filter(c => c.type === 'expense')} isGlobal={false} />
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', textTransform: 'uppercase' }}>
              Global Categories <span style={{ fontWeight: 400, textTransform: 'none' }}>(shared · read-only)</span>
            </div>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <CatList label="Income"  list={globals.filter(c => c.type === 'income')}  isGlobal={true} />
              <CatList label="Expense" list={globals.filter(c => c.type === 'expense')} isGlobal={true} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Bank balances ─────────────────────────────────────────────────────────────

function BalanceManager({ balances, onSave, onDelete }) {
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm]     = useState({ bank_name: '', balance: '', currency: 'INR', as_of_date: today(), notes: '' });

  const handleSave = async () => {
    if (!form.bank_name.trim() || !form.balance) return;
    setSaving(true);
    await onSave(form);
    setForm({ bank_name: '', balance: '', currency: 'INR', as_of_date: today(), notes: '' });
    setSaving(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Bank / Account Balances</span>
        <span style={{ color: '#555', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <input style={{ ...S.input, flex: '1 1 160px' }} placeholder="Bank or account name"
              value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} />
            <input style={{ ...S.input, flex: '0 0 120px' }} type="number" placeholder="Balance"
              value={form.balance} onChange={e => setForm(p => ({ ...p, balance: e.target.value }))} />
            <select style={S.select} value={form.currency}
              onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>)}
            </select>
            <input style={{ ...S.input, flex: '0 0 140px' }} type="date"
              value={form.as_of_date} onChange={e => setForm(p => ({ ...p, as_of_date: e.target.value }))} />
            <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
          {balances.length === 0 && <div style={{ color: '#444', fontSize: '13px' }}>No balances added yet.</div>}
          {balances.map(b => (
            <div key={b.id} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '6px', fontSize: '13px' }}>
              <span style={{ flex: 1 }}>{b.bank_name}</span>
              <span style={{ color: '#64b5f6', fontWeight: 600 }}>{fmtAmount(b.balance, b.currency)}</span>
              <span style={{ ...S.badgeCurr }}>{(b.currency || 'INR').toUpperCase()}</span>
              <span style={{ color: '#555' }}>{b.as_of_date}</span>
              <button style={S.btnDanger} onClick={() => onDelete(b.id)}>✕</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Add transaction form ──────────────────────────────────────────────────────

function AddTransactionForm({ categories, onAdd }) {
  const lastCurr = localStorage.getItem('et_currency') || 'INR';
  const [open, setOpen] = useState(true);  // open by default
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    date: today(), description: '', amount: '', currency: lastCurr,
    type: 'expense', category_id: '', notes: '',
  });

  const set = (k, v) => {
    if (k === 'currency') localStorage.setItem('et_currency', v);
    setForm(p => ({ ...p, [k]: v }));
  };

  const filtered       = categories.filter(c => c.type === form.type);
  const filteredGlobal = filtered.filter(c => c.is_global);
  const filteredUser   = filtered.filter(c => !c.is_global);

  const handleAdd = async () => {
    if (!form.description.trim() || !form.amount || !form.date) return;
    setAdding(true);
    const cat = categories.find(c => c.id === parseInt(form.category_id));
    await onAdd({
      ...form,
      amount: parseFloat(form.amount),
      category_id:   cat ? cat.id   : null,
      category_name: cat ? cat.name : 'Miscellaneous',
      is_misc: !cat,
      source: 'manual',
    });
    setForm(p => ({ ...p, description: '', amount: '', category_id: '', notes: '' }));
    setAdding(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Add Transaction</span>
        <span style={{ color: '#555', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div>
          {/* Row 1: Date | Type | Amount | Currency */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={S.label}>Date</div>
              <input style={S.input} type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div>
              <div style={S.label}>Type</div>
              <select style={{ ...S.input, cursor: 'pointer' }} value={form.type}
                onChange={e => { set('type', e.target.value); set('category_id', ''); }}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div>
              <div style={S.label}>Amount</div>
              <input style={S.input} type="number" placeholder="0.00"
                value={form.amount} onChange={e => set('amount', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </div>
            <div>
              <div style={S.label}>Currency</div>
              <select style={{ ...S.input, cursor: 'pointer' }} value={form.currency} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Description | Category | Notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div>
              <div style={S.label}>Description *</div>
              <input style={S.input} placeholder="e.g. Grocery shopping"
                value={form.description} onChange={e => set('description', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </div>
            <div>
              <div style={S.label}>Category</div>
              <select style={{ ...S.input, cursor: 'pointer' }} value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                <option value="">— None —</option>
                {filteredUser.length > 0 && (
                  <optgroup label="Your Categories">
                    {filteredUser.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                )}
                {filteredGlobal.length > 0 && (
                  <optgroup label="Global">
                    {filteredGlobal.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <div style={S.label}>Notes (optional)</div>
              <input style={S.input} placeholder="Remarks…"
                value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>

          <button style={S.btnPrimary} onClick={handleAdd} disabled={adding}>
            {adding ? 'Adding…' : '+ Add Transaction'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Bank statement uploader ───────────────────────────────────────────────────

function StatementUploader({ categories, onImport }) {
  const [open, setOpen]           = useState(false);
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [pdfUrl, setPdfUrl]       = useState(null);
  const [parsing, setParsing]     = useState(false);
  const [parsed, setParsed]       = useState([]);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg]             = useState(null);
  const [bulkCurr, setBulkCurr]  = useState('');
  const fileRef                   = useRef();

  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const handleFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setFile(f); setParsed([]); setPreview(null); setBulkCurr('');
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    if (f.name.toLowerCase().endsWith('.pdf')) {
      setPdfUrl(URL.createObjectURL(f));
    } else {
      const r = new FileReader();
      r.onload = ev => setPreview((ev.target.result || '').slice(0, 3000));
      r.readAsText(f);
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category_names', categories.map(c => c.name).join(','));
      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/finance/parse-statement', {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Parse failed');
      const dc = data.detected_currency || localStorage.getItem('et_currency') || 'INR';
      setParsed((data.transactions || []).map(t => ({ ...t, currency: t.currency || dc })));
      flash(`Found ${data.count} transactions. Review and import.`);
    } catch (err) { flash(err.message, 'error'); }
    finally { setParsing(false); }
  };

  const edit   = (i, k, v) => setParsed(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const remove = (i)        => setParsed(prev => prev.filter((_, idx) => idx !== i));

  const applyBulk = (curr) => { if (curr) setParsed(prev => prev.map(r => ({ ...r, currency: curr }))); };

  const handleImport = async () => {
    if (!parsed.length) return;
    setImporting(true);
    await onImport(parsed);
    setParsed([]); setFile(null); setPreview(null); setBulkCurr('');
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    if (fileRef.current) fileRef.current.value = '';
    flash(`${parsed.length} transactions imported!`);
    setImporting(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Upload Bank Statement</span>
        <span style={{ color: '#555', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          <Flash msg={msg} onDismiss={() => setMsg(null)} />
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" accept=".pdf,.csv,.txt,.tsv" onChange={handleFile} style={{ color: '#ccc', fontSize: '13px' }} />
            {file && (
              <button style={S.btnPrimary} onClick={handleParse} disabled={parsing}>
                {parsing ? 'Parsing with AI…' : '🤖 Parse with AI'}
              </button>
            )}
          </div>

          {pdfUrl && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>PDF Preview — PII masked before AI processing</div>
              <iframe src={pdfUrl} title="Statement" style={{ width: '100%', height: '360px', border: '1px solid #333', borderRadius: '6px' }} />
            </div>
          )}
          {preview && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>File preview — PII masked before AI processing</div>
              <pre style={{ background: '#12121e', border: '1px solid #2a2a3e', borderRadius: '6px', padding: '12px', fontSize: '11px', color: '#aaa', overflowX: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{preview}</pre>
            </div>
          )}

          {parsed.length > 0 && (
            <>
              {/* Bulk currency control */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', padding: '10px 14px', background: '#12121e', borderRadius: '8px', border: '1px solid #2a2a3e' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Set all rows to:</span>
                <select style={S.select} value={bulkCurr} onChange={e => setBulkCurr(e.target.value)}>
                  <option value="">— pick currency —</option>
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
                <button style={{ ...S.btnSecondary, opacity: bulkCurr ? 1 : 0.4 }} disabled={!bulkCurr} onClick={() => applyBulk(bulkCurr)}>
                  Apply to all
                </button>
              </div>

              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                Review parsed transactions — edit inline, remove unwanted rows, then import.
              </div>
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>Description</th>
                      <th style={S.th}>Amount</th>
                      <th style={S.th}>Currency</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Category</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((row, i) => (
                      <tr key={i} style={row.is_misc ? S.trMisc : {}}>
                        <td style={S.td}><input type="date" style={{ ...S.input, width: '130px' }} value={row.date} onChange={e => edit(i, 'date', e.target.value)} /></td>
                        <td style={S.td}><input style={{ ...S.input, width: '200px' }} value={row.description} onChange={e => edit(i, 'description', e.target.value)} /></td>
                        <td style={S.td}><input type="number" style={{ ...S.input, width: '100px' }} value={row.amount} onChange={e => edit(i, 'amount', e.target.value)} /></td>
                        <td style={S.td}>
                          <select style={S.select} value={row.currency || 'INR'} onChange={e => edit(i, 'currency', e.target.value)}>
                            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>
                          <select style={S.select} value={row.type} onChange={e => edit(i, 'type', e.target.value)}>
                            <option value="expense">Expense</option>
                            <option value="income">Income</option>
                          </select>
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '140px' }} value={row.category_name}
                            onChange={e => { edit(i, 'category_name', e.target.value); edit(i, 'is_misc', e.target.value === 'Miscellaneous'); }} />
                          {row.is_misc && <span style={S.badgeMisc}>MISC</span>}
                        </td>
                        <td style={S.td}><button style={S.btnDanger} onClick={() => remove(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
                <button style={S.btnPrimary} onClick={handleImport} disabled={importing}>
                  {importing ? 'Importing…' : `⬇ Import ${parsed.length} transactions`}
                </button>
                <button style={S.btnSecondary} onClick={() => setParsed([])}>Clear</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Transaction row (inline-editable) ─────────────────────────────────────────

function TxRow({ tx, categories, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({
    date: tx.date || '', description: tx.description || '', amount: tx.amount || '',
    currency: tx.currency || 'INR', type: tx.type || 'expense',
    category_id: tx.category_id || '', notes: tx.notes || '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const filtered       = categories.filter(c => c.type === form.type);
  const filteredGlobal = filtered.filter(c => c.is_global);
  const filteredUser   = filtered.filter(c => !c.is_global);

  const handleSave = async () => {
    setSaving(true);
    const cat = categories.find(c => c.id === parseInt(form.category_id));
    await onSave(tx.id, {
      ...form,
      amount:        parseFloat(form.amount),
      category_id:   cat ? cat.id   : null,
      category_name: cat ? cat.name : (tx.category_name || 'Miscellaneous'),
      is_misc:       !cat && (!tx.category_name || tx.category_name === 'Miscellaneous'),
    });
    setSaving(false); setEditing(false);
  };

  const cat      = categories.find(c => c.id === tx.category_id);
  const dotColor = cat ? cat.color : '#607d8b';

  if (editing) {
    return (
      <tr style={tx.is_misc ? S.trMisc : {}}>
        <td style={S.td}><input type="date" style={{ ...S.input, width: '130px' }} value={form.date} onChange={e => set('date', e.target.value)} /></td>
        <td style={S.td}><input style={{ ...S.input, width: '200px' }} value={form.description} onChange={e => set('description', e.target.value)} /></td>
        <td style={S.td}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input type="number" style={{ ...S.input, width: '90px' }} value={form.amount} onChange={e => set('amount', e.target.value)} />
            <select style={{ ...S.select, fontSize: '12px', padding: '5px 6px' }} value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
        </td>
        <td style={S.td}>
          <select style={S.select} value={form.type} onChange={e => { set('type', e.target.value); set('category_id', ''); }}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
        </td>
        <td style={S.td}>
          <select style={S.select} value={form.category_id} onChange={e => set('category_id', e.target.value)}>
            <option value="">— None —</option>
            {filteredUser.length > 0 && (
              <optgroup label="Your Categories">
                {filteredUser.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
            {filteredGlobal.length > 0 && (
              <optgroup label="Global">
                {filteredGlobal.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
          </select>
        </td>
        <td style={S.td}><input style={{ ...S.input, width: '120px' }} placeholder="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} /></td>
        <td style={S.td}>
          <button style={{ ...S.btnPrimary, marginRight: '4px' }} onClick={handleSave} disabled={saving}>{saving ? '…' : '✓'}</button>
          <button style={S.btnSmall} onClick={() => setEditing(false)}>✕</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={tx.is_misc ? S.trMisc : {}} onDoubleClick={() => setEditing(true)} title="Double-click to edit">
      <td style={S.td}>{tx.date}</td>
      <td style={S.td}>
        {tx.description}
        {tx.is_misc && <span style={S.badgeMisc}>MISC</span>}
      </td>
      <td style={{ ...S.td, fontWeight: 600, color: tx.type === 'income' ? '#4caf50' : '#f44336', whiteSpace: 'nowrap' }}>
        {fmtAmount(tx.amount, tx.currency)}
        <span style={S.badgeCurr}>{(tx.currency || 'INR').toUpperCase()}</span>
      </td>
      <td style={S.td}>
        <span style={tx.type === 'income' ? S.badgeIncome : S.badgeExpense}>
          {tx.type.toUpperCase()}
        </span>
      </td>
      <td style={S.td}>
        {tx.category_name && <><span style={S.dot(dotColor)} />{tx.category_name}</>}
      </td>
      <td style={{ ...S.td, color: '#555', fontSize: '12px' }}>{tx.notes || '—'}</td>
      <td style={S.td}>
        <button style={{ ...S.btnSmall, marginRight: '4px' }} onClick={() => setEditing(true)} title="Edit">✎</button>
        <button style={S.btnDanger} onClick={() => onDelete(tx.id)} title="Delete">✕</button>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ExpenseTracker() {
  const [transactions, setTransactions] = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [balances,     setBalances]     = useState([]);
  const [summary,      setSummary]      = useState({ total_income: 0, total_expense: 0, savings: 0, breakdown: [] });
  const [loading,      setLoading]      = useState(true);
  const [msg,          setMsg]          = useState(null);

  const now = new Date();
  const [year,         setYear]        = useState(now.getFullYear());
  const [month,        setMonth]       = useState(now.getMonth() + 1);
  const [typeFilter,   setTypeFilter]  = useState('all');
  const [currFilter,   setCurrFilter]  = useState('all');
  const [baseCurrency, setBaseCurrency]= useState('');

  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadTransactions = useCallback(() => {
    const qs = new URLSearchParams({ year, month });
    if (typeFilter !== 'all') qs.set('type', typeFilter);
    if (currFilter !== 'all') qs.set('currency', currFilter);
    return authFetch(`/api/finance/transactions?${qs}`)
      .then(r => r.json()).then(d => setTransactions(d.transactions || [])).catch(() => {});
  }, [year, month, typeFilter, currFilter]);

  const loadSummary = useCallback(() => {
    const qs = new URLSearchParams({ year, month });
    if (currFilter !== 'all') qs.set('currency', currFilter);
    if (baseCurrency)         qs.set('base_currency', baseCurrency);
    return authFetch(`/api/finance/summary?${qs}`)
      .then(r => r.json()).then(d => setSummary(d)).catch(() => {});
  }, [year, month, currFilter, baseCurrency]);

  const loadCategories = useCallback(() =>
    authFetch('/api/finance/categories').then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {}), []);

  const loadBalances = useCallback(() =>
    authFetch('/api/finance/opening-balance').then(r => r.json()).then(d => setBalances(d.balances || [])).catch(() => {}), []);

  const refreshAll = useCallback(() => {
    setLoading(true);
    Promise.all([loadCategories(), loadBalances(), loadTransactions(), loadSummary()]).finally(() => setLoading(false));
  }, [loadCategories, loadBalances, loadTransactions, loadSummary]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const addCategory = async (form) => {
    try {
      const r = await authFetch('/api/finance/categories', { method: 'POST', body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadCategories(); flash('Category added');
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Transactions will be marked Miscellaneous.')) return;
    try {
      await authFetch(`/api/finance/categories/${id}`, { method: 'DELETE' });
      loadCategories(); loadTransactions(); flash('Category deleted');
    } catch (e) { flash(e.message, 'error'); }
  };

  const addTransaction = async (body) => {
    try {
      const r = await authFetch('/api/finance/transactions', { method: 'POST', body: JSON.stringify(body) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadTransactions(); loadSummary(); flash('Transaction added');
    } catch (e) { flash(e.message, 'error'); }
  };

  const saveTransaction = async (id, body) => {
    try {
      const r = await authFetch(`/api/finance/transactions/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadTransactions(); loadSummary();
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await authFetch(`/api/finance/transactions/${id}`, { method: 'DELETE' });
      loadTransactions(); loadSummary(); flash('Transaction deleted');
    } catch (e) { flash(e.message, 'error'); }
  };

  const saveBalance = async (form) => {
    try {
      const r = await authFetch('/api/finance/opening-balance', { method: 'POST', body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadBalances(); flash('Balance saved');
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteBalance = async (id) => {
    if (!window.confirm('Remove this balance?')) return;
    try {
      await authFetch(`/api/finance/opening-balance/${id}`, { method: 'DELETE' });
      loadBalances(); flash('Balance removed');
    } catch (e) { flash(e.message, 'error'); }
  };

  const importParsed = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      try {
        const cat = categories.find(c => c.name === row.category_name);
        const r = await authFetch('/api/finance/transactions', {
          method: 'POST', body: JSON.stringify({ ...row, category_id: cat ? cat.id : null }),
        });
        if (r.ok) ok++;
      } catch (_) {}
    }
    loadTransactions(); loadSummary();
    flash(`${ok} of ${rows.length} transactions imported`);
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const isMultiCurrency = (summary.breakdown || []).length > 1;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Expense &amp; Income Tracker</h1>
      <p style={S.subtitle}>Track your monthly cash flow across multiple currencies. Double-click any row to edit.</p>

      <Flash msg={msg} onDismiss={() => setMsg(null)} />

      {/* Filter bar */}
      <div style={S.filterRow}>
        <div>
          <div style={S.filterLabel}>Month</div>
          <select style={S.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <div style={S.filterLabel}>Year</div>
          <select style={S.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={S.filterLabel}>Type</div>
          <select style={S.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            <option value="income">Income only</option>
            <option value="expense">Expenses only</option>
          </select>
        </div>
        <div>
          <div style={S.filterLabel}>Currency</div>
          <select style={S.select} value={currFilter} onChange={e => setCurrFilter(e.target.value)}>
            <option value="all">All currencies</option>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>)}
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button style={S.btnSecondary} onClick={refreshAll}>↻ Refresh</button>
        </div>
      </div>

      {/* Summary */}
      <SummaryCards summary={summary} balances={balances} />

      {/* Converted totals — only when multi-currency */}
      {isMultiCurrency && (
        <ConvertedSummary
          summary={summary}
          baseCurrency={baseCurrency}
          setBaseCurrency={(c) => { setBaseCurrency(c); }}
        />
      )}

      {/* Add transaction */}
      <AddTransactionForm categories={categories} onAdd={addTransaction} />

      {/* Upload statement */}
      <StatementUploader categories={categories} onImport={importParsed} />

      {/* Bank balances */}
      <BalanceManager balances={balances} onSave={saveBalance} onDelete={deleteBalance} />

      {/* Categories */}
      <CategoryManager categories={categories} onAdd={addCategory} onDelete={deleteCategory} />

      {/* Transaction list */}
      <div style={S.section}>
        <div style={{ ...S.sectionHead, cursor: 'default', marginBottom: '16px' }}>
          <span style={S.sectionTitle}>
            Transactions — {MONTHS[month - 1]} {year}
            <span style={{ color: '#444', fontWeight: 400, fontSize: '13px', marginLeft: '8px' }}>
              ({transactions.length} records)
            </span>
          </span>
        </div>

        {loading && <div style={{ color: '#555', fontSize: '13px' }}>Loading…</div>}

        {!loading && transactions.length === 0 && (
          <div style={{ color: '#444', fontSize: '13px' }}>
            No transactions for this period. Add one above or upload a bank statement.
          </div>
        )}

        {transactions.length > 0 && (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Amount</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Category</th>
                  <th style={S.th}>Notes</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <TxRow
                    key={tx.id} tx={tx} categories={categories}
                    onSave={saveTransaction} onDelete={deleteTransaction}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
